/**
 * Cover and uncover (docs/spec/04 §4): at a cave's start every cell is hidden under flickering cover
 * tiles that dissolve at random, one cell per row per 40 ms; at its end they return the same way.
 */

import { Rng } from '../sim/rng';

export const COVER_STEP_MS = 40;

export class Cover {
  private covered: Uint8Array = new Uint8Array(0);
  /** Per row, the columns in the order they change. */
  private order: number[][] = [];
  private next = 0;
  private acc = 0;
  private w = 0;
  private h = 0;
  /** 'uncover' reveals, 'cover' hides, 'idle' does nothing. */
  mode: 'uncover' | 'cover' | 'idle' = 'idle';

  /** Start fully covered (uncover) or fully visible (cover). */
  start(mode: 'uncover' | 'cover', w: number, h: number, seed: number): void {
    this.w = w;
    this.h = h;
    this.mode = mode;
    this.covered = new Uint8Array(w * h).fill(mode === 'uncover' ? 1 : 0);
    const rng = new Rng(seed);
    this.order = Array.from({ length: h }, () => {
      const cols = Array.from({ length: w }, (_, i) => i);
      for (let i = cols.length - 1; i > 0; i--) {
        const j = rng.int(i + 1);
        [cols[i], cols[j]] = [cols[j] ?? 0, cols[i] ?? 0];
      }
      return cols;
    });
    this.next = 0;
    this.acc = 0;
  }

  /** Everything visible, nothing running. */
  clear(): void {
    this.mode = 'idle';
    this.covered = new Uint8Array(0);
  }

  update(dtMs: number): void {
    if (this.mode === 'idle') return;
    this.acc += dtMs;
    while (this.acc >= COVER_STEP_MS && !this.done) {
      this.acc -= COVER_STEP_MS;
      for (let y = 0; y < this.h; y++) {
        const x = this.order[y]?.[this.next];
        if (x !== undefined) this.covered[y * this.w + x] = this.mode === 'uncover' ? 0 : 1;
      }
      this.next++;
    }
  }

  /** The animation has run its course (all revealed, or all hidden). */
  get done(): boolean {
    return this.mode === 'idle' || this.next >= this.w;
  }

  /** Is any cell still hidden (or already hidden again)? */
  get active(): boolean {
    return this.mode !== 'idle' && this.covered.length > 0;
  }

  isCovered(x: number, y: number): boolean {
    return this.covered[y * this.w + x] === 1;
  }

  /** How long a full pass takes for a cave `w` cells wide. */
  static durationMs(w: number): number {
    return w * COVER_STEP_MS;
  }
}
